import React, { useEffect, useMemo, useState } from "react";
import { Modal, Tag, Tooltip, Button, Skeleton, message } from "antd";
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CameraOutlined,
  UserOutlined,
  BranchesOutlined,
  PlusOutlined,
  MinusOutlined,
  SwapOutlined,
  CommentOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { getChanges, updateIssueStatus } from "api/issue/issueApi";

const T = {
  ink: "#23343E",
  text: "#3A4952",
  muted: "#8D8D8D",
  sub: "#AEAEAE",
  line: "#EFEFEF",
  line2: "#E2E2E2",
  bg: "#FAFAFA",
  wash: "#E9F1F5",
  navy: "#455D6B",
  navyD: "#3A5765",
  orange: "#FE6919",
  added: "#4FC92A",
  addedBg: "#EEFBE9",
  added2: "#317A1B",
  removed: "#FB8080",
  removedBg: "#FFF0F0",
  removed2: "#C53030",
  mod: "#71A0B8",
  modBg: "#E9F1F5",
  mod2: "#3A5765",
};

function Pill({ kind = "neutral", children, size = "md" }) {
  const pad = size === "sm" ? "2px 7px" : "3px 9px";
  const fs = size === "sm" ? 9.5 : 10.5;
  const map = {
    neutral: { bg: "#F3F4F5", bd: "#E8EAEC", fg: "#526470" },
    navy: { bg: T.wash, bd: "#D4E3EC", fg: T.navyD },
    added: { bg: T.addedBg, bd: "#CDEEC1", fg: T.added2 },
    removed: { bg: T.removedBg, bd: "#FAD6D6", fg: T.removed2 },
    modified: { bg: T.modBg, bd: "#C8DDE5", fg: T.mod2 },
    orange: { bg: "#FFF2E9", bd: "#FFD9BF", fg: "#B84B0A" },
  };
  const s = map[kind] || map.neutral;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      background: s.bg, border: `1px solid ${s.bd}`, color: s.fg,
      padding: pad, borderRadius: 999,
      fontSize: fs, fontWeight: 600, lineHeight: 1, letterSpacing: ".02em",
    }}>{children}</span>
  );
}

function DiffTriplet({ added = 0, removed = 0, modified = 0 }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 10, fontSize: 12, fontWeight: 600 }}>
      <span style={{ color: T.added2 }}>+{added}</span>
      <span style={{ color: T.removed2 }}>−{removed}</span>
      <span style={{ color: T.mod2 }}>~{modified}</span>
    </span>
  );
}

function StackBar({ added = 0, removed = 0, modified = 0 }) {
  const sum = added + removed + modified;
  if (!sum) return <div style={{ height: 6, background: "#F3F4F5", borderRadius: 999 }} />;
  const pa = (added / sum) * 100;
  const pm = (modified / sum) * 100;
  const pr = (removed / sum) * 100;
  return (
    <div style={{ display: "flex", height: 6, borderRadius: 999, overflow: "hidden", background: "#F3F4F5" }}>
      <div style={{ width: `${pa}%`, background: T.added }} />
      <div style={{ width: `${pm}%`, background: T.mod }} />
      <div style={{ width: `${pr}%`, background: T.removed }} />
    </div>
  );
}

function ModelViewer3D({ height = 260, showDiff = true, beforeLabel = "BEFORE", afterLabel = "AFTER" }) {
  return (
    <div style={{
      position: "relative", height, background: "#1A1A1A",
      border: "1px solid #0C0C0C", borderRadius: 6, overflow: "hidden",
    }}>
      <svg viewBox="0 0 600 400" style={{ width: "100%", height: "100%", display: "block" }}>
        <defs>
          <linearGradient id="mdm-sky" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#2A3744" /><stop offset="1" stopColor="#141C24" />
          </linearGradient>
        </defs>
        <rect width="600" height="400" fill="url(#mdm-sky)" />
        <g stroke="#3A4A5A" strokeWidth=".6">
          {Array.from({ length: 12 }).map((_, i) => (
            <line key={`h${i}`} x1={0} y1={260 + i * 12} x2={600} y2={260 + i * 12} />
          ))}
          {Array.from({ length: 20 }).map((_, i) => (
            <line key={`v${i}`} x1={i * 30 - 50 + i * 3} y1={260} x2={i * 30 + 150} y2={392} />
          ))}
        </g>
        <g>
          <polygon points="160,230 340,210 440,240 260,260" fill="#5A6B7A" stroke="#23343E" strokeWidth="1.5" />
          <polygon points="160,230 260,260 260,330 160,300" fill="#455D6B" stroke="#23343E" strokeWidth="1.5" />
          <polygon points="260,260 440,240 440,310 260,330" fill="#71A0B8" stroke="#23343E" strokeWidth="1.5" />
          <polygon points="220,160 380,145 450,170 290,185" fill="#6B7D8C" stroke="#23343E" strokeWidth="1.5" />
          <polygon points="220,160 290,185 290,230 220,205" fill="#4E6472" stroke="#23343E" strokeWidth="1.5" />
          <polygon points="290,185 450,170 450,215 290,230" fill="#88A8BD" stroke="#23343E" strokeWidth="1.5" />
          {showDiff && (
            <>
              <polygon
                points="220,160 380,145 450,170 290,185"
                fill="none" stroke={T.removed} strokeWidth="2"
                strokeDasharray="4 3" transform="translate(-10,4)"
              />
              <circle cx="340" cy="195" r="32" fill={T.added} fillOpacity=".2" />
              <circle cx="340" cy="195" r="10" fill={T.added} />
              <text x="340" y="199" textAnchor="middle" fontSize="13" fontWeight="700" fill="#fff">✓</text>
            </>
          )}
        </g>
      </svg>
      {showDiff && (
        <div style={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 6 }}>
          <span style={{
            background: "rgba(251,128,128,.9)", color: "#fff",
            padding: "3px 8px", borderRadius: 3,
            fontSize: 10, fontWeight: 600, letterSpacing: ".04em",
          }}>{beforeLabel}</span>
          <span style={{
            background: "rgba(79,201,42,.9)", color: "#fff",
            padding: "3px 8px", borderRadius: 3,
            fontSize: 10, fontWeight: 600, letterSpacing: ".04em",
          }}>{afterLabel}</span>
        </div>
      )}
      <div style={{
        position: "absolute", bottom: 8, left: 8, right: 8,
        display: "flex", justifyContent: "space-between", alignItems: "center",
        fontSize: 10, color: "rgba(255,255,255,.55)",
      }}>
        <span>preview · mock 3D scene</span>
        <span>Orbit · Pan · Zoom</span>
      </div>
    </div>
  );
}

function UnifiedPropertyDiff({ changes }) {
  if (!changes || changes.length === 0) {
    return (
      <div style={{
        padding: "20px 16px", textAlign: "center", color: T.muted,
        fontSize: 11, fontStyle: "italic", border: `1px dashed ${T.line2}`, borderRadius: 6,
      }}>
        No property changes recorded for this revision.
      </div>
    );
  }
  return (
    <div style={{
      border: `1px solid ${T.line}`, borderRadius: 6, overflow: "hidden",
      fontFamily: "'SF Mono', ui-monospace, Menlo, Consolas, monospace",
      fontSize: 11.5, background: "#fff",
    }}>
      {changes.map((el, idx) => (
        <div key={el.uniqueId || idx} style={{ borderTop: idx === 0 ? "none" : `1px solid ${T.line}` }}>
          {/* element header */}
          <div style={{
            padding: "8px 12px", background: "#F9FAFB",
            borderBottom: `1px solid ${T.line}`,
            display: "flex", alignItems: "center", gap: 8,
          }}>
            <Pill kind={el.op === "added" ? "added" : el.op === "removed" ? "removed" : "modified"} size="sm">
              {el.op === "added" ? <><PlusOutlined /> ADDED</> : el.op === "removed" ? <><MinusOutlined /> REMOVED</> : <><SwapOutlined /> MODIFIED</>}
            </Pill>
            <span style={{
              fontFamily: "'SF Mono', ui-monospace, Menlo, Consolas, monospace",
              fontSize: 11, fontWeight: 600, color: T.ink,
            }}>{el.category}</span>
            <span style={{ fontSize: 10.5, color: T.muted }}>
              uniqueId: <span style={{ color: T.text }}>{el.uniqueId}</span>
            </span>
            {el.name && <span style={{ fontSize: 11, color: T.text, marginLeft: "auto" }}>{el.name}</span>}
          </div>
          {/* property diff rows */}
          <div>
            {(el.props || []).map((p, i) => (
              <div key={i}>
                {p.before != null && (
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "32px 180px 1fr",
                    background: T.removedBg,
                    borderLeft: `3px solid ${T.removed}`,
                    padding: "4px 8px 4px 10px",
                    color: T.removed2,
                  }}>
                    <span style={{ color: T.removed2, fontWeight: 700 }}>−</span>
                    <span style={{ color: T.muted }}>{p.key}</span>
                    <span style={{ fontWeight: 500 }}>{String(p.before)}</span>
                  </div>
                )}
                {p.after != null && (
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "32px 180px 1fr",
                    background: T.addedBg,
                    borderLeft: `3px solid ${T.added}`,
                    padding: "4px 8px 4px 10px",
                    color: T.added2,
                  }}>
                    <span style={{ color: T.added2, fontWeight: 700 }}>+</span>
                    <span style={{ color: T.muted }}>{p.key}</span>
                    <span style={{ fontWeight: 500 }}>{String(p.after)}</span>
                  </div>
                )}
                {p.before == null && p.after == null && (
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "32px 180px 1fr",
                    padding: "4px 8px 4px 10px",
                    color: T.muted,
                  }}>
                    <span>~</span>
                    <span>{p.key}</span>
                    <span style={{ fontStyle: "italic" }}>{p.note || "changed"}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ResolveChecklist({ hasScreenshot, hasTaggedUsers, hasCommit, needsCommit }) {
  const row = (ok, label, hint) => (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "6px 0" }}>
      <span style={{
        width: 16, height: 16, borderRadius: "50%",
        background: ok ? T.added : T.line2,
        color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center",
        fontSize: 10, fontWeight: 700, flexShrink: 0, marginTop: 1,
      }}>{ok ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 11.5, fontWeight: 600, color: ok ? T.ink : "#B84B0A" }}>{label}</div>
        {hint && <div style={{ fontSize: 10.5, color: T.muted, marginTop: 2 }}>{hint}</div>}
      </div>
    </div>
  );
  return (
    <div style={{ padding: "4px 12px 8px", background: T.bg, border: `1px solid ${T.line}`, borderRadius: 6 }}>
      <div style={{
        fontSize: 10.5, fontWeight: 600, color: T.muted,
        letterSpacing: ".06em", margin: "10px 2px 4px",
      }}>REQUIRED TO RESOLVE</div>
      {row(hasScreenshot, "Screenshot attached", "Required on every resolution.")}
      {row(hasTaggedUsers, "At least one user tagged", "Confirms who signed off.")}
      {needsCommit && row(hasCommit, "Linked model commit", "3D-linked resolutions must reference a diff.")}
    </div>
  );
}

// Build a diff summary from a issue. Uses real fields when present,
// otherwise falls back to a minimal shape. Backend integration point:
// pass `diff` as a prop once the API exposes revision-diff data.
function deriveDiffSummary(issue, diffProp) {
  if (diffProp) return diffProp;
  const is3D = issue?.sourceType === "modelViewer";
  const objectCount = issue?.issueObjectsCount || 0;
  return {
    is3D,
    beforeLabel: "BEFORE",
    afterLabel: "AFTER",
    stats: {
      added: 0,
      removed: 0,
      modified: objectCount,
      costImpact: null,
    },
    elements: [],
  };
}

export default function IssueDiffModal({ visible, issue, onClose, diff, onStatusChanged }) {
  const [tab, setTab] = useState("viewer");
  const [activity, setActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [resolving, setResolving] = useState(false);
  const summary = useMemo(() => deriveDiffSummary(issue, diff), [issue, diff]);

  useEffect(() => {
    if (!visible || !issue?._id) { setActivity(null); return; }
    let cancelled = false;
    setActivityLoading(true);
    getChanges(issue._id, { limit: 20 })
      .then(res => { if (!cancelled) setActivity(res); })
      .catch(() => { if (!cancelled) setActivity({ changes: [] }); })
      .finally(() => { if (!cancelled) setActivityLoading(false); });
    return () => { cancelled = true; };
  }, [visible, issue?._id]);

  if (!issue) return null;

  const needsCommit = summary.is3D;
  const hasScreenshot = !!issue.thumbnail;
  const hasTaggedUsers = (issue.responsibleUsers || []).length > 0;
  const hasCommit = !!summary.commitSha;
  const isResolved = issue.status === "resolved";
  const canResolve = hasScreenshot && hasTaggedUsers && (!needsCommit || hasCommit) && !isResolved;

  const handleMarkResolved = async () => {
    if (!canResolve) {
      message.warning("Resolution requirements not met");
      return;
    }
    setResolving(true);
    try {
      await updateIssueStatus(issue._id, "resolved");
      message.success("Marked resolved");
      if (onStatusChanged) onStatusChanged("resolved");
      if (onClose) onClose();
    } catch (e) {
      message.error(e.message || "Failed to mark resolved");
    } finally {
      setResolving(false);
    }
  };

  const tabs = [
    { key: "viewer", label: "3D Diff", icon: <BranchesOutlined /> },
    { key: "elements", label: `Elements (${summary.elements?.length || 0})`, icon: <SwapOutlined /> },
    { key: "resolution", label: "Resolution", icon: <CheckCircleOutlined /> },
  ];

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1100}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{
            background: T.navy, color: "#fff", padding: "3px 8px", borderRadius: 3,
            fontSize: 11, fontWeight: 700,
          }}>{issue._id?.slice(-6).toUpperCase() || "—"}</span>
          <span style={{ fontSize: 15, fontWeight: 600, color: T.ink }}>{issue.title}</span>
          <Pill kind={summary.is3D ? "navy" : "neutral"} size="sm">
            {summary.is3D ? "3D DIFF" : "2D DIFF"}
          </Pill>
        </div>
      }
      bodyStyle={{ padding: 0 }}
    >
      {/* stats row */}
      <div style={{
        padding: "12px 20px", borderBottom: `1px solid ${T.line}`,
        background: T.bg,
        display: "grid", gridTemplateColumns: "repeat(4, 1fr) auto", gap: 16, alignItems: "center",
      }}>
        <div>
          <div style={{ fontSize: 10.5, color: T.muted, fontWeight: 600, letterSpacing: ".06em", marginBottom: 4 }}>CHANGES</div>
          <DiffTriplet added={summary.stats.added} removed={summary.stats.removed} modified={summary.stats.modified} />
          <div style={{ marginTop: 6 }}>
            <StackBar added={summary.stats.added} removed={summary.stats.removed} modified={summary.stats.modified} />
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10.5, color: T.muted, fontWeight: 600, letterSpacing: ".06em", marginBottom: 4 }}>COST IMPACT</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: summary.stats.costImpact == null ? T.muted : (summary.stats.costImpact >= 0 ? T.added2 : T.removed2) }}>
            {summary.stats.costImpact == null ? "—" :
              `${summary.stats.costImpact >= 0 ? "+" : "−"}$${Math.abs(summary.stats.costImpact).toLocaleString()}`}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10.5, color: T.muted, fontWeight: 600, letterSpacing: ".06em", marginBottom: 4 }}>COMMIT</div>
          <div style={{
            fontFamily: "'SF Mono', ui-monospace, Menlo, Consolas, monospace",
            fontSize: 12, color: summary.commitSha ? T.navy : T.muted, fontWeight: 600,
          }}>{summary.commitSha || "—"}</div>
        </div>
        <div>
          <div style={{ fontSize: 10.5, color: T.muted, fontWeight: 600, letterSpacing: ".06em", marginBottom: 4 }}>SOURCE</div>
          <div style={{ fontSize: 12, color: T.text }}>
            {issue.sourceType === "modelViewer" ? "3D Model" : issue.sourceType === "docControl" ? "2D Sheet" : "Blank"}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10.5, color: T.muted, fontWeight: 600, letterSpacing: ".06em", marginBottom: 4 }}>ASSIGNED</div>
          {(issue.responsibleUsers || []).length === 0 ? (
            <span style={{ color: T.sub, fontSize: 12 }}>—</span>
          ) : (
            <div style={{ display: "flex", gap: 2 }}>
              {(issue.responsibleUsers || []).slice(0, 4).map((u, i) => (
                <Tooltip key={u._id || i} title={u.name || u.email}>
                  <div style={{
                    width: 22, height: 22, borderRadius: "50%", background: T.navy,
                    color: "#fff", fontSize: 10, fontWeight: 600,
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    border: "1.5px solid #fff", marginLeft: i === 0 ? 0 : -6,
                  }}>
                    {(u.name || u.email || "?").charAt(0).toUpperCase()}
                  </div>
                </Tooltip>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* tab bar */}
      <div style={{ display: "flex", borderBottom: `1px solid ${T.line}` }}>
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              border: "none", background: "transparent",
              padding: "10px 16px", cursor: "pointer",
              fontSize: 12, fontWeight: 600,
              color: tab === t.key ? T.navy : T.muted,
              borderBottom: tab === t.key ? `2px solid ${T.navy}` : "2px solid transparent",
              display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* body */}
      <div style={{ padding: 20, maxHeight: "calc(85 * var(--vh) - 260px)", overflowY: "auto" }}>
        {tab === "viewer" && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: T.muted, letterSpacing: ".06em" }}>
                MODEL DIFF · BEFORE / AFTER
              </span>
              {summary.revLink && <Pill kind="navy" size="sm">{summary.revLink}</Pill>}
            </div>
            <ModelViewer3D height={300} showDiff beforeLabel={summary.beforeLabel} afterLabel={summary.afterLabel} />
            {issue.thumbnail && (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "16px 0 8px" }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: T.muted, letterSpacing: ".06em" }}>
                    ISSUE SCREENSHOT
                  </span>
                  <Pill kind="added" size="sm"><CameraOutlined /> ATTACHED</Pill>
                </div>
                <div style={{
                  position: "relative", maxHeight: 240, background: "#F3F4F6",
                  border: `1px solid ${T.line}`, borderRadius: 6, overflow: "hidden",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <img src={issue.thumbnail} alt="Issue" style={{ maxWidth: "100%", maxHeight: 240, objectFit: "contain" }} />
                </div>
              </>
            )}
          </>
        )}

        {tab === "elements" && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: T.muted, letterSpacing: ".06em" }}>
                ELEMENT-LEVEL CHANGES
              </span>
              <DiffTriplet added={summary.stats.added} removed={summary.stats.removed} modified={summary.stats.modified} />
            </div>
            <UnifiedPropertyDiff changes={summary.elements} />
          </>
        )}

        {tab === "resolution" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: T.muted, letterSpacing: ".06em", marginBottom: 8 }}>
                STATUS
              </div>
              <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                <Tag color={
                  issue.status === "resolved" ? "green" :
                  issue.status === "pending" ? "orange" :
                  issue.status === "abandoned" ? "default" : "blue"
                } style={{ fontSize: 12, padding: "4px 10px" }}>
                  {(issue.status || "open").toUpperCase()}
                </Tag>
                {isResolved ? (
                  <Pill kind="added" size="sm"><CheckCircleOutlined /> RESOLVED</Pill>
                ) : canResolve ? (
                  <Pill kind="added" size="sm">✓ READY TO RESOLVE</Pill>
                ) : (
                  <Pill kind="orange" size="sm">MISSING REQUIREMENTS</Pill>
                )}
              </div>
              <ResolveChecklist
                hasScreenshot={hasScreenshot}
                hasTaggedUsers={hasTaggedUsers}
                hasCommit={hasCommit}
                needsCommit={needsCommit}
              />
              <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                <Button
                  type="primary"
                  disabled={!canResolve}
                  loading={resolving}
                  onClick={handleMarkResolved}
                  style={{ background: canResolve ? T.added2 : undefined, borderColor: canResolve ? T.added2 : undefined }}
                >
                  Mark resolved
                </Button>
                <Button onClick={onClose}>Close</Button>
              </div>

              <div style={{ fontSize: 11, fontWeight: 600, color: T.muted, letterSpacing: ".06em", margin: "20px 0 8px" }}>
                TAGGED USERS
              </div>
              {(issue.responsibleUsers || []).length === 0 ? (
                <div style={{ fontSize: 12, color: T.sub, fontStyle: "italic" }}>
                  No users tagged. Required before resolving.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {issue.responsibleUsers.map((u, i) => (
                    <div key={u._id || i} style={{
                      display: "flex", alignItems: "center", gap: 8,
                      padding: "6px 8px", background: T.bg,
                      border: `1px solid ${T.line}`, borderRadius: 4,
                    }}>
                      <UserOutlined style={{ color: T.navy }} />
                      <span style={{ fontSize: 12, color: T.text, fontWeight: 500 }}>
                        {u.name || u.email?.split("@")[0]}
                      </span>
                      {u.email && <span style={{ fontSize: 10.5, color: T.muted }}>{u.email}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: T.muted, letterSpacing: ".06em", marginBottom: 8 }}>
                ACTIVITY ({activity?.changes?.length || 0})
              </div>
              {activityLoading ? (
                <Skeleton active paragraph={{ rows: 4 }} />
              ) : !activity?.changes?.length ? (
                <div style={{ fontSize: 12, color: T.sub, fontStyle: "italic" }}>
                  No activity recorded.
                </div>
              ) : (
                <div style={{
                  display: "flex", flexDirection: "column", gap: 8,
                  maxHeight: 380, overflowY: "auto", paddingRight: 4,
                }}>
                  {activity.changes.slice(0, 20).map((c, i) => {
                    const who = c.userId?.name || c.guestId?.name || "Unknown";
                    const isResolveEvent = c.changeType === "resolved" || c.changeType === "status_change";
                    return (
                      <div key={c._id || i} style={{ display: "flex", gap: 8 }}>
                        <div style={{
                          width: 22, height: 22, borderRadius: "50%",
                          background: isResolveEvent ? T.added : T.navy,
                          color: "#fff", fontSize: 10, fontWeight: 600,
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                          flexShrink: 0,
                        }}>{who.charAt(0).toUpperCase()}</div>
                        <div style={{
                          flex: 1,
                          background: isResolveEvent ? T.addedBg : "#fff",
                          border: `1px solid ${isResolveEvent ? "#CDEEC1" : T.line2}`,
                          borderRadius: 6, padding: "6px 10px",
                        }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                            <span style={{ fontSize: 11, fontWeight: 600, color: T.ink }}>{who}</span>
                            <Pill
                              kind={isResolveEvent ? "added" : c.changeType === "delete" ? "removed" : "neutral"}
                              size="sm"
                            >
                              {c.changeType || "edit"}
                            </Pill>
                            <span style={{ flex: 1 }} />
                            <span style={{ fontSize: 10, color: T.muted }}>
                              {c.timestamp ? moment(c.timestamp).fromNow() : ""}
                            </span>
                          </div>
                          <div style={{ fontSize: 11.5, color: T.text, lineHeight: 1.4 }}>
                            {c.description || `${c.changeType} on ${c.objectType || "issue"}`}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
